import { Injectable, inject } from '@angular/core';
import { MsalService } from '@azure/msal-angular';
import { AccountInfo } from '@azure/msal-browser';
import { HandleError, HttpErrorHandler, HttpUtility } from '@core/http';
import { IAuthenticationService, IUserClaims } from '@mynexus/mfe-core';
import { IAuthConfig } from '@env/IEnvironment';
import { API_BASE_PLATFORM, AUTH_CONFIG } from '@core/config/config.service';
import { lastValueFrom, map, Observable, tap } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class AuthenticationService implements IAuthenticationService {
  private msalService = inject(MsalService);
  private httpUtility = inject(HttpUtility);
  private httpErrorHandler = inject(HttpErrorHandler);
  private authConfig = inject<IAuthConfig>(AUTH_CONFIG);
  private platformApiUrl = inject(API_BASE_PLATFORM);

  isPermissionsInitialized = false;

  public handleError: HandleError;
  private permissions: string[] = [];

  constructor() {
    this.handleError = this.httpErrorHandler.createHandleError('AuthenticationService');
  }

  public loadUserPermissions(): Observable<string[]> {
    return this.httpUtility
      .post<Record<string, string[]>>(`${this.platformApiUrl}/Permissions`)
      .pipe(
        map(res => Object.values(res).flat()),
        tap(permissions => {
          this.permissions = permissions
          this.isPermissionsInitialized = true
        })
      );
  }

  get userPermissions(): string[] {
    return this.permissions;
  }

  getToken(): Promise<string> {
    return new Promise<string>((resolve, reject) => {
      this.msalService
        .acquireTokenSilent({
          scopes: this.authConfig.angularConfig.protectedResourceMap[0][1],
          account: this.user
        })
        .subscribe({
          next: ({ accessToken }) => resolve(accessToken),
          error: reject
        });
    });
  }

  get userClaims(): IUserClaims {
    return this.claims || {};
  }
  get userEmail(): string | null {
    return this.email;
  }
  get userGroups(): string[] {
    return this.claims?.groups || [];
  }

  get user(): AccountInfo {
    return this.msalService.instance.getAllAccounts()[0];
  }

  get userName(): string {
    // tslint:disable-next-line: no-non-null-assertion
    return this.user ? this.user.name! : '';
  }

  get userTenantId(): string | undefined {
    return this.user?.tenantId;
  }

  get claims(): IUserClaims | null {
    // @ts-ignore
    return this.user?.idTokenClaims ? (this.user.idTokenClaims as IUserClaims) : null;
  }

  get groups(): string[] | undefined {
    return this.claims?.groups;
  }

  get email(): string | null {
    const claims = this.claims;

    if (claims?.emails) {
      const emails = claims.emails;

      if (emails.length > 0) {
        return emails[0];
      }
    }

    return null;
  }

  get isExternalUser(): boolean {
    if (this.claims) {
      return !!this.claims.extension_isExternal;
    }

    return false;
  }

  get token(): string | undefined {
    return this.user?.idToken;
  }

  public logout(): void {
    this.msalService.logoutRedirect();
  }
}
